import React, { useContext, useEffect, useRef } from 'react';
import router from 'next/router';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { costFormatter } from '@format/currency';
import { ServerSideErrorModal, LoadingView } from '@web/components';
import {
  freshModalsContent,
  dryModalsContent,
} from '@web/testing/modalConstants';
import { getVariantsSortedByPrice, truncatePrice } from '../../products';
import { getAge, getBreed, getActivity } from '../../pets';
import { ApiResult, wrapErrorWithResult } from '../../api';
import { UserInfoForm } from '../UserInfoForm';
import { PupInfoForm } from '../PupInfoForm';
import { BirthdayForm } from '../BirthdayForm';
import { BreedForm } from '../BreedForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import {
  RecommendedMealPlanForm,
  RecommendedPlanContainer,
} from '../RecommendedPlanForm';
import planHeaderPhoto from '../../images/product-photos/pack-shots/pack-on-board-with-shadow.png';
import { PlanSelectionContainer } from '../PlanSelectionContainer';
import { PlanTypeSelectionForm } from '../PlanTypeSelectionForm';
import { FreshRecipesSelectionForm } from '../FreshRecipesSelectionForm';
import { DryRecipesSelectionForm } from '../DryRecipesSelectionForm';
import { AddOnSelectionForm } from '../AddOnSelectionForm';
import { QuizHeader } from '../QuizHeader';
import {
  recipes as freshRecipes,
  dryRecipes,
  mealPlanModalContent,
  MEAL_PREP_TYPES,
} from '../constants';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { calculateDiscountedPrice } from '../../offer/helpers';
import {
  userSkipsAddonSelection,
  userContinuesWithAddonSelection,
} from './events';
import {
  STATE,
  ACTIONS,
  levelToLabel,
  ONBOARDING_VARIATIONS,
  MEAL_PREP_CRO_DATA,
  RECOMMENDED_PLAN_CRO_DATA,
  BC_TO_SHOPIFY_SKU_MAP,
} from './constants';
import { Main, Wrapper } from './styles';
import { OnboardingContext } from './context';
import { interpolations } from './interpolation';
import {
  onCartSubmission,
  getLowestCostVariant,
  getRecommendedMealplanProduct,
  getRecipeCombination,
  getUnavailableRecipes,
} from './functions';

const QuizContainer = ({
  stepNumber,
  children,
  steps = 4,
  variation = 'control',
}) => {
  // if (variation === ONBOARDING_VARIATIONS.required) {
  //   steps = 4;
  // }
  const { showModal, setShowModal } = useContext(OnboardingContext);
  return (
    <Wrapper>
      <QuizHeader currentStep={stepNumber} totalSteps={steps} />
      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  steps: PropTypes.number,
  stepNumber: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
};

const UserInfo = props => {
  const {
    customer,
    pet,
    send,
    attachCustomer,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    goBack,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);

  const defaultValues = {
    firstName: customer?.first_name || '',
    email: customer?.email || '',
  };

  const onSubmit = async ({ firstName, email }) => {
    let result;

    try {
      //* Payload returns the entire cart object which includes the customer and pets
      let payload = await attachCustomer({
        ...(firstName ? { first_name: firstName } : {}),
        last_name: 'Fake-LastName',
        email,
      });

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(
          ACTIONS.customerInfoSaved({
            customer: payload.customer,
            pet: payload.pets[0],
            pets: payload.pets,
            cart: payload ?? null,
          }),
        );
      },
      error: {
        //* 404 Error (Session ID not found)
        //* 400 Error (Invalid payload)
        //* 422 Error (Internal error)
        client: error => reporter.error(error.original),
        //* 401 Error (Existing customer)
        existingCustomer: () => goToLogin(),
        //* 500 error (All other errors)
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 0,
  };

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.userInfo}
      variation={shortenedOnboardingQuiz}
    >
      <UserInfoForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        petName={pet?.name}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupInfo = props => {
  const {
    pet,
    savePet,
    send,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);
  const defaultValues = {
    gender: pet?.gender,
    name: pet?.name,
  };

  const onSubmit = async ({ gender, name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender, name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toCalories({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 1,
  };

  const goBack = () => router.push('/');

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.pupInfo}
      variation={shortenedOnboardingQuiz}
    >
      <PupInfoForm
        {...cmsProps}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
      />
    </QuizContainer>
  );
};

const Birthday = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);

  const [year, month, day] = pet.birth_date?.split('-') ?? ['', '', ''];
  const defaultValues = {
    year: year,
    month: month,
    day: day,
  };

  const onSubmit = async ({ year, month, day }) => {
    const birthDate = `${year}-${month}-${+day || 1}`;
    let result, payload;
    try {
      const response = await savePet({ birth_date: birthDate });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (shortenedOnboardingQuiz === ONBOARDING_VARIATIONS.control) {
          send(ACTIONS.toBreed({ pet: payload.pet }));
        } else {
          send(ACTIONS.toCalories({ pet: payload.pet }));
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 2,
  };

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.birthday}
      variation={shortenedOnboardingQuiz}
    >
      <BirthdayForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Breed = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    isLocalPet,
  } = useContext(OnboardingContext);

  const defaultValues = {
    breeds:
      pet.breeds?.map(breed => ({
        value: breed.id,
        label: breed.name,
      })) ?? [],
  };

  const onSubmit = async ({ breeds }) => {
    const newBreeds = breeds.map(breed =>
      isLocalPet ? { id: breed.value, name: breed.label } : breed.value,
    );
    let result, payload;
    try {
      const response = await savePet({ breeds: newBreeds });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toCalories({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 3,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.breeds}>
      <BreedForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Calories = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);
  const defaultValues = {
    weight: pet?.weight,
    idealWeight: pet?.ideal_weight,
    spayedStatus: pet?.is_neutered,
  };

  const onSubmit = async ({ weight, idealWeight, spayedStatus }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
        is_neutered: spayedStatus,
      });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toActivity({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 4,
  };

  const isPuppy = false;

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.calories}
      variation={shortenedOnboardingQuiz}
    >
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const {
    pet,
    customer,
    pets,
    cart,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);
  const defaultValues = {
    activity: pet.activity_level,
  };
  const petsRef = useRef(pets);
  petsRef.current = pets;
  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      // todo someting here to save to api then clear pet from local regardless of allergies, let's just add the pet to teh cart if allergie is needed
      const response = await savePet({ activity_level: activity });

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (!customer?.first_name) {
          send(ACTIONS.toUserInfo({ pet: payload.pet }));
        } else {
          send(
            ACTIONS.toRecommendedPlan({
              customer,
              pet: payload.pet,
              pets: petsRef.current,
              cart,
            }),
          );
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 5,
  };
  return (
    <QuizContainer
      stepNumber={quizStepNumbers.activity}
      variation={shortenedOnboardingQuiz}
    >
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PlanSelection = ({ planSelectionInfo, body }) => {
  const {
    pet,
    returnToQuiz,
    goBack,
    send,
    service,
    pricePeriod,
    availablePlanTypes,
    isAppSession,
  } = useContext(OnboardingContext);
  const { type, amount } = useShopifyOfferContext();

  const productTypeInterpolationSubstitutes = {
    pupName: pet.name,
    // Base prices from the API
    mixedPricePerWeek: 50,
    freshPricePerWeek: 50,
    halfFreshPricePerWeek: 50,
    bakedPricePerWeek: 50,
    // Calculate discounted prices using the offer context
    mixedDiscountPricePerWeek: calculateDiscountedPrice(50, type, amount),
    freshDiscountPricePerWeek: calculateDiscountedPrice(50, type, amount),
    halfFreshDiscountPricePerWeek: calculateDiscountedPrice(50, type, amount),
    bakedDiscountPricePerWeek: calculateDiscountedPrice(50, type, amount),
  };

  const onSubmit = async planType => {
    send(
      ACTIONS.selectedPlanType({
        planType,
        mealplanId: availablePlanTypes[planType]?.mealplan_id,
      }),
    );
    return onCartSubmission(service);
  };
  body = MEAL_PREP_CRO_DATA.body;
  planSelectionInfo = MEAL_PREP_CRO_DATA.planSelectionInfo;

  // The default plan type will be determined by the CMS
  let defaultPlanType = 'fresh';
  if (planSelectionInfo.items?.length) {
    // Assuming there's a non-collapsed item, this should be the assumed default
    const planType = planSelectionInfo.items[0];
    defaultPlanType = planType.value;
  }

  const unavailablePlanTypes = [];
  if (
    planSelectionInfo.items.length &&
    planSelectionInfo.collapsedItems.length
  ) {
    const allPlanTypes = [
      ...planSelectionInfo.items,
      ...planSelectionInfo.collapsedItems,
    ];
    allPlanTypes.forEach(planType => {
      // We still call this dry in a lot of places so this is hacky, but we can probably correct this post-Shopify
      const planTypeValue = planType.value === 'dry' ? 'baked' : planType.value;
      // If the plan type is in the plan types map, but not returned by the CMS we're not counting it as unavailable
      if (!availablePlanTypes[planTypeValue])
        unavailablePlanTypes.push(planTypeValue);
    });
  }

  return (
    <PlanSelectionContainer body={body}>
      <PlanTypeSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupActivity: getActivity(pet.activity_level),
        })}
        planTypes={interpolate(
          planSelectionInfo.items,
          productTypeInterpolationSubstitutes,
        )}
        collapsedPlanTypes={interpolate(
          planSelectionInfo.collapsedItems,
          productTypeInterpolationSubstitutes,
        )}
        defaultPlanType={defaultPlanType}
        unavailablePlanTypes={unavailablePlanTypes}
        returnToQuiz={returnToQuiz}
        goBack={isAppSession ? null : goBack}
        onSubmit={onSubmit}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

PlanSelection.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
  noRecipesInfo: PropTypes.object,
};

const getUnavailableMealPrepTypes = combinationsByPlanType => {
  return MEAL_PREP_TYPES.filter(type => {
    const planType = combinationsByPlanType[type];
    return (
      !planType ||
      !planType.planCombinations ||
      planType.planCombinations.length === 0
    );
  });
};

const RecommendedPlan = () => {
  const {
    setShowModal,
    reporter,
    combinationsByPlanType,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
    setLocalSelection,
    mealPlanForPet,
    flags: { recommendedPlanCRO },
  } = useContext(OnboardingContext);
  const { type, amount, formattedAmount } = useShopifyOfferContext();
  let combinationId = null;
  const defaultPlanType = mealPlanForPet
    ? mealPlanForPet.plan_type_name
    : 'fresh';
  const defaultFreshRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selections.fresh
    : combinationsByPlanType.fresh?.recommendedCombination?.recipes?.fresh ??
      [];
  const defaultBakedRecipes =
    mealPlanForPet?.plan_type_name === 'mixed'
      ? mealPlanForPet.recipe_selections.baked
      : combinationsByPlanType.mixed?.recommendedCombination?.recipes?.baked ??
        [];

  const unavailableFreshRecipes = getUnavailableRecipes(
    combinationsByPlanType.fresh?.availableRecipes?.fresh || [],
  );
  const unavailableBakedRecipes = getUnavailableRecipes(
    combinationsByPlanType.baked?.availableRecipes?.baked || [],
  );

  const unavailableMealPrepTypes = getUnavailableMealPrepTypes(
    combinationsByPlanType,
  );

  const getConfigFromRecipes = (
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  ) => {
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      const product = combinationsByPlanType[selectedMealPrepType];

      if (!selectedFreshRecipes.length)
        return { price: null, message: 'Choose one or more recipes.' };

      const variants =
        product && Array.isArray(product.planCombinations)
          ? getVariantsSortedByPrice(product.planCombinations)
          : [];
      const newVariant = getRecipeCombination(variants, {
        fresh: selectedFreshRecipes,
        baked: [],
      });

      if (!newVariant)
        return {
          price: null,
          message: 'No matching recipe combination found.',
        };

      const price = newVariant.price_per_week;
      const discountedPrice = calculateDiscountedPrice(price, type, amount);
      combinationId = newVariant.combination_id;

      return {
        price,
        discountedPrice,
      };
    } else {
      const product = combinationsByPlanType['mixed'];

      if (!selectedFreshRecipes.length)
        return {
          price: null,
          message: 'Choose one or more fresh recipes.',
        };

      if (!selectedBakedRecipes.length)
        return {
          price: null,
          message: 'Choose one or more baked recipes.',
        };

      const variants = getVariantsSortedByPrice(product.planCombinations);
      const newVariant = getRecipeCombination(variants, {
        fresh: selectedFreshRecipes,
        baked: selectedBakedRecipes,
      });

      if (!newVariant)
        return {
          price: null,
          message: 'No matching recipe combination found.',
        };

      const price = newVariant.price_per_week;
      const discountedPrice = calculateDiscountedPrice(price, type, amount);
      combinationId = newVariant.combination_id;

      return {
        price,
        discountedPrice,
      };
    }
  };
  const lowestMixedVariant = combinationsByPlanType?.mixed?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.mixed.planCombinations)
    : null;
  let lowestFreshVariant = combinationsByPlanType?.fresh?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.fresh.planCombinations)
    : null;
  const lowestHalfFreshVariant = combinationsByPlanType?.half_fresh
    ?.planCombinations?.length
    ? getLowestCostVariant(combinationsByPlanType.half_fresh.planCombinations)
    : null;
  const lowestBakedVariant = combinationsByPlanType?.dry?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.dry.planCombinations)
    : null;

  const productTypeInterpolationSubstitutes = {
    freshPricePerWeek: lowestFreshVariant?.price_per_week,
    freshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestFreshVariant?.price_per_week,
      type,
      amount,
    ),
    mixedPricePerWeek: lowestMixedVariant?.price_per_week,
    mixedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestMixedVariant?.price_per_week,
      type,
      amount,
    ),
    halfFreshPricePerWeek: lowestHalfFreshVariant?.price_per_week,
    halfFreshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestHalfFreshVariant?.price_per_week,
      type,
      amount,
    ),
    bakedPricePerWeek: lowestBakedVariant?.price_per_week,
    bakedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestBakedVariant?.price_per_week,
      type,
      amount,
    ),
  };

  const onMealPlanAndRecipesSubmit = async (
    planType,
    freshRecipes,
    bakedRecipes,
  ) => {
    if (planType === 'mixed') {
      send(
        ACTIONS.selectedMixedRecipes(
          {
            recipes: {
              dry: bakedRecipes,
              fresh: freshRecipes,
              planType: planType,
            },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
          },
        ),
      );
    } else {
      send(
        ACTIONS.selectedFreshRecipes(
          { recipes: freshRecipes, planType: planType },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
          },
        ),
      );
    }
    return onCartSubmission(service);
  };

  const mealPrepData =
    recommendedPlanCRO === 'full_fresh_only'
      ? interpolate(
          MEAL_PREP_CRO_DATA.planSelectionInfo.items,
          productTypeInterpolationSubstitutes,
        )
      : interpolate(
          MEAL_PREP_CRO_DATA.planSelectionInfoRecommendedPage.items,
          productTypeInterpolationSubstitutes,
        );

  return (
    <RecommendedPlanContainer>
      <RecommendedMealPlanForm
        onSubmit={onMealPlanAndRecipesSubmit}
        headline={`Recommended for ${pet.name}`}
        subheadline={`As a ${pet.weight}-pound ${
          levelToLabel[pet.activity_level]
        } pup, we've created meals perfect for ${
          pet.name
        }'s complete nutrition needs.`}
        ctaText={`Continue with ${formattedAmount} off`}
        modals={[...freshModalsContent, ...dryModalsContent]}
        freshRecipes={freshRecipes}
        defaultFreshRecipes={defaultFreshRecipes ?? []}
        bakedRecipes={dryRecipes}
        defaultBakedRecipes={defaultBakedRecipes ?? []}
        defaultMealPrepType={defaultPlanType}
        mealPrepTypes={mealPrepData}
        collapsedMealPrepTypes={interpolate(
          MEAL_PREP_CRO_DATA.planSelectionInfoRecommendedPage.collapsedItems,
          productTypeInterpolationSubstitutes,
        )}
        unavailableMealPrepTypes={unavailableMealPrepTypes}
        unavailableFreshRecipes={unavailableFreshRecipes}
        unavailableBakedRecipes={unavailableBakedRecipes}
        onBackButtonClick={goBack}
        period={pricePeriod}
        getConfigFromRecipes={getConfigFromRecipes}
        splitScreenPromoProps={RECOMMENDED_PLAN_CRO_DATA}
        headerImage={planHeaderPhoto}
        modalContent={interpolate(
          mealPlanModalContent,
          productTypeInterpolationSubstitutes,
        )}
        notSelectableText={`Based on your answers, this meal plan is unavailable for ${pet.name}.`}
        hideHalfFresh={recommendedPlanCRO === 'fresh_and_mixed'}
        showFreshOnly={recommendedPlanCRO === 'full_fresh_only'}
      />
    </RecommendedPlanContainer>
  );
};

RecommendedPlan.displayName = 'RecommendedPlan';
RecommendedPlan.propTypes = {};

const Fresh = ({ planSelectionInfo, body, noRecipesInfo }) => {
  const {
    reporter,
    mealplanData,
    mealPlanId,
    mealPlanForPet,
    setShowModal,
    setLocalSelection,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
  } = useContext(OnboardingContext);
  const { type, amount } = useShopifyOfferContext();

  const recommendedMealplanCombination =
    getRecommendedMealplanProduct(mealplanData);

  const defaultRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selection.fresh
    : recommendedMealplanCombination.recipes.fresh;

  let combinationId = recommendedMealplanCombination.combination_id;
  const onSubmit = async freshRecipes => {
    send(
      ACTIONS.selectedFreshRecipes(
        { recipes: freshRecipes },
        {
          combinationId,
          mealplanData,
          mealPlanId,
          mealPlanForPet,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );
    return onCartSubmission(service);
  };

  const getPlanCombinationConfigFromRecipes = selectedRecipes => {
    if (!selectedRecipes.length)
      return { message: 'Choose one or more recipes.' };

    const variants = mealplanData.plan_combinations;

    const newVariant = getRecipeCombination(variants, {
      fresh: selectedRecipes,
      baked: recommendedMealplanCombination.recipes.baked,
    });

    const price = newVariant.subscription_data.price_per_week;
    const discountedPrice = calculateDiscountedPrice(price, type, amount);
    combinationId = newVariant.combination_id;

    return {
      price: costFormatter(price),
      discountedPrice: costFormatter(discountedPrice),
    };
  };
  const unavailableRecipes = getUnavailableRecipes(
    mealplanData.available_recipes.fresh,
  );
  const checkoutBarHeadline =
    pet.name.toUpperCase() +
    "'S " +
    recommendedMealplanCombination.weeks_of_food +
    '-WEEK STARTER BOX';
  return (
    <PlanSelectionContainer body={body}>
      <FreshRecipesSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupAge: getAge(pet.birth_date),
          pupActivity: levelToLabel[pet.activity_level],
          pupBreed: getBreed(pet.breeds),
          pupAllergies: pet.allergies,
          numStarterBoxWeeks: recommendedMealplanCombination.weeks_of_food,
        })}
        checkoutBarHeadline={checkoutBarHeadline}
        defaultRecipes={defaultRecipes}
        onSubmit={onSubmit}
        onBackButtonClick={goBack}
        unavailableRecipes={unavailableRecipes}
        getConfigFromRecipes={getPlanCombinationConfigFromRecipes}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

Fresh.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
  noRecipesInfo: PropTypes.object,
};

const Baked = ({ planSelectionInfo, body }) => {
  const {
    reporter,
    selectedFreshRecipes,
    mealplanData,
    mealPlanForPet,
    setShowModal,
    setLocalSelection,
    pet,
    pet_id: petId,
    mealPlanId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
  } = useContext(OnboardingContext);
  const { type, amount } = useShopifyOfferContext();

  const recommendedMealplanCombination =
    getRecommendedMealplanProduct(mealplanData);

  const defaultRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selection.baked
    : recommendedMealplanCombination.recipes.baked;
  let combinationId = recommendedMealplanCombination.combination_id;
  const onSubmit = async dryRecipes => {
    send(
      ACTIONS.selectedBakedRecipes(
        { recipes: dryRecipes },
        {
          combinationId,
          mealPlanId,
          mealplanData,
          mealPlanForPet,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );

    return onCartSubmission(service);
  };

  const getPlanCombinationConfigFromRecipes = selectedRecipes => {
    if (!selectedRecipes.length)
      return { message: 'Choose one or more recipes.' };

    const variants = mealplanData.plan_combinations;

    const newVariant = getRecipeCombination(variants, {
      fresh: selectedFreshRecipes,
      baked: selectedRecipes,
    });

    const price = newVariant.subscription_data.price_per_week;
    const discountedPrice = calculateDiscountedPrice(price, type, amount);
    combinationId = newVariant.combination_id;

    return {
      price: costFormatter(price),
      discountedPrice: costFormatter(discountedPrice),
    };
  };

  const unavailableRecipes = getUnavailableRecipes(
    mealplanData.available_recipes.fresh,
  );

  const checkoutBarHeadline =
    pet.name.toUpperCase() +
    "'S " +
    recommendedMealplanCombination.weeks_of_food +
    '-WEEK STARTER BOX';

  return (
    <PlanSelectionContainer body={body}>
      <DryRecipesSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupAge: getAge(pet.birth_date),
          pupActivity: levelToLabel[pet.activity_level],
          pupBreed: getBreed(pet.breeds),
          pupAllergies: pet.allergies,
          numStarterBoxWeeks: recommendedMealplanCombination.weeks_of_food,
        })}
        checkoutBarHeadline={checkoutBarHeadline}
        defaultRecipes={defaultRecipes}
        onSubmit={onSubmit}
        onBackButtonClick={goBack}
        unavailableRecipes={unavailableRecipes}
        getConfigFromRecipes={getPlanCombinationConfigFromRecipes}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

Baked.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
};

const Addons = ({
  headline,
  subheadline,
  body,
  addonSections,
  modals,
  desktopCtaText,
}) => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    attachExtras,
    send,
    selectedAddons: defaultAddons,
    treatProducts,
    dentalChews,
    supplementProducts,
  } = useContext(OnboardingContext);
  const { formattedAmount, type, amount } = useShopifyOfferContext();
  const treatsAndDentalChews = [...treatProducts, ...dentalChews];

  const findProductBySku = (products, sku) => {
    const product = products.find(product => product.sku === sku);
    return product;
  };

  const mergedAddonSection = addonSections.map((section, i) => {
    const addons = section.addons
      .map(addon => {
        const productType = addon.productType;
        const products =
          productType === 'TR' || productType === 'DC'
            ? treatsAndDentalChews
            : supplementProducts;

        const mappedSku = BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku;
        const product = findProductBySku(products, mappedSku);

        if (!product) {
          return null;
        }

        const discountedPrice = calculateDiscountedPrice(
          product.price,
          type,
          amount,
        );

        return interpolate(addon, {
          addonPrice: truncatePrice(product.price),
          addonDiscount: truncatePrice(discountedPrice),
          petName: pet.name,
          addonSavePercentage: formattedAmount,
        });
      })
      .filter(Boolean);

    return {
      ...section,
      addons,
    };
  });

  const mergedModals = modals
    .map(modal => {
      const product = findProductBySku(
        [...supplementProducts, ...treatsAndDentalChews],
        BC_TO_SHOPIFY_SKU_MAP[modal.sku] || modal.sku,
      );
      if (!product) return null;

      const discountedPrice = calculateDiscountedPrice(
        product.price,
        type,
        amount,
      );

      return interpolate(modal, {
        addonPrice: truncatePrice(product.price),
        addonDiscount: truncatePrice(discountedPrice),
        addonSavePercentage: formattedAmount,
      });
    })
    .filter(Boolean);

  const onSkip = () => {
    reporter.tag(userSkipsAddonSelection());
    send(ACTIONS.toCheckout());
  };

  const onSubmit = async selectedAddons => {
    let result = ApiResult.success();
    const attachResult = await attachExtras(
      petId,
      selectedAddons.map(addon => ({
        sku: BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku,
        frequency: addon.frequency,
        quantity: addon.quantity,
        recurrence: addon.recurrence,
      })),
    );

    result = ApiResult.success(attachResult)
      ? attachResult
      : ApiResult.error.client('Failed to attach extras', attachResult.error);

    ApiResult.match(result, {
      success: () => {
        reporter.tag(userContinuesWithAddonSelection(selectedAddons));
        send(ACTIONS.toCheckout());
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const unavailableAddons = [];

  return (
    <PlanSelectionContainer body={body}>
      <AddOnSelectionForm
        headline={interpolate(headline, { pupName: pet.name })}
        subheadline={subheadline}
        addonSections={mergedAddonSection}
        defaultAddons={defaultAddons}
        unavailableAddons={unavailableAddons}
        onSubmit={onSubmit}
        onSkipButtonClick={onSkip}
        onBackButtonClick={goBack}
        submitButtonText={desktopCtaText}
        modals={mergedModals}
      />
    </PlanSelectionContainer>
  );
};

Addons.propTypes = {
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  body: PropTypes.array,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = () => {
  const { cart, customer } = useContext(OnboardingContext);
  const checkoutUrl = cart?.checkout_url;
  const customerEmail = customer?.email;
  useEffect(() => {
    // To maintain user continuity between domains, send the anonymousId to our Shopify Segment instance
    let anonymousId = '';
    if (window.analytics && window.analytics.user) {
      anonymousId = window.analytics.user().anonymousId();
    }
    if (checkoutUrl) {
      const url = checkoutUrl?.split('?')?.[0];
      const search = window.location?.search?.split('?')?.[1] || '';
      const initialParams = (search && `&${search}`) || '';
      const anonymousIdParam =
        (anonymousId && `&segmentAnonymousId=${anonymousId}`) || '';
      const checkoutEmailParam =
        (customerEmail &&
          `&checkout[email]=${encodeURIComponent(customerEmail)}`) ||
        '';
      // There's always at minimum 1 query parameter (key) in a Shopify checkout url
      const queryParams =
        checkoutUrl.split('?')?.[1] +
        initialParams +
        anonymousIdParam +
        checkoutEmailParam;
      window.location.href = url + `?${queryParams}`;
    }
  }, [customerEmail, checkoutUrl]);
  return <LoadingView variant="checkout" />;
};

export const components = {
  [STATE.userInfo]: UserInfo,
  [STATE.pupInfo]: PupInfo,
  [STATE.birthday]: Birthday,
  [STATE.breed]: Breed,
  [STATE.calories]: Calories,
  [STATE.activity]: Activity,
  // [STATE.allergies]: Allergies,
  // [STATE.poop]: Poop,
  [STATE.recommendedPlan]: RecommendedPlan,
  [STATE.planSelection]: PlanSelection,
  [STATE.fresh]: Fresh,
  [STATE.baked]: Baked,
  [STATE.addons]: Addons,
  [STATE.checkout]: Checkout,
};
